import { ALL_COLOR_IDS, type ColorId } from './colors';
import { PROTOCOLS, type ProtocolConfig } from './protocols';
import type { Language, Question, VoiceCue } from './types';
import { type Rng, pickOne, randomInt, shuffle } from './rng';

export interface GenerateQuestionOptions {
  id: string;
  protocol: ProtocolConfig;
  rng: Rng;
  questionIndex: number;
  forceVoice?: boolean;
}

export function colorsForProtocol(protocol: ProtocolConfig): ColorId[] {
  return ALL_COLOR_IDS.slice(0, protocol.colorCount) as ColorId[];
}

function chooseLanguage(protocol: ProtocolConfig, rng: Rng): Language {
  if (protocol.id === 1) return 'zh';
  if (protocol.id === 2) return rng() < 0.28 ? 'en' : 'zh';
  if (protocol.id === 3) return rng() < 0.46 ? 'en' : 'zh';
  return rng() < 0.54 ? 'en' : 'zh';
}

function chooseVoiceColor(
  rng: Rng,
  pool: readonly ColorId[],
  ink: ColorId,
  word: ColorId,
): ColorId {
  const roll = rng();
  const wrongColors = pool.filter((color) => color !== ink && color !== word);
  if (roll < 0.5 && wrongColors.length > 0) return pickOne(rng, wrongColors);
  if (roll < 0.74) return word;
  if (roll < 0.9) return ink;
  return pickOne(rng, pool);
}

function makeVoice(
  enabled: boolean,
  force: boolean,
  rng: Rng,
  pool: readonly ColorId[],
  ink: ColorId,
  word: ColorId,
): VoiceCue | undefined {
  if (!enabled) return undefined;
  if (!force && rng() > 0.82) return undefined;
  const language: Language = rng() < 0.5 ? 'zh' : 'en';
  const color = chooseVoiceColor(rng, pool, ink, word);
  return {
    lang: language,
    color,
    clip: `/audio/voice/${language}-${color}.wav`,
  };
}

export function generateQuestion(options: GenerateQuestionOptions): Question {
  const { protocol, rng } = options;
  const pool = colorsForProtocol(protocol);
  const word = pickOne(rng, pool);
  let ink: ColorId;
  if (rng() < protocol.incongruentChance) {
    ink = pickOne(
      rng,
      pool.filter((color) => color !== word),
    );
  } else {
    ink = word;
  }

  const optionOrder = shuffle(rng, pool);
  const language = chooseLanguage(protocol, rng);
  const voice = makeVoice(protocol.speech, options.forceVoice ?? false, rng, pool, ink, word);

  return {
    id: options.id,
    protocol: protocol.id,
    word,
    ink,
    language,
    options: optionOrder,
    timeLimitMs: protocol.timeLimitMs,
    ...(voice ? { voice } : {}),
  };
}

/**
 * 唯一的判题口径：屏幕上那行字的墨色决定按哪个键，
 * 按键上写的颜色词是答案，按键自己的字色是干扰。
 */
export function resolveQuestionAnswer(question: Question): ColorId {
  return question.ink;
}

export function randomQuestionId(rng: Rng): string {
  return `${Date.now().toString(36)}-${randomInt(rng, 0x7fffffff).toString(36)}`;
}
